import { COLLISION_CELLS_PER_TILE } from "../document/types.ts";
import { cellFootprint } from "./coords.ts";
import { lineCells } from "./shapes.ts";

/**
 * The zone brush in whole tiles (ADR-054's amendment of 2026-10-01, decision 1): the Pencil, Square and Line of
 * Zones mode work on the tile grid and paint every collision cell of each tile they cover, so a zone edge lies on
 * a tile edge. Each function takes the map's size in tiles and returns row-major, duplicate-free indices into
 * the collision grid, `COLLISION_CELLS_PER_TILE` times as wide and as tall.
 */

/**
 * A rectangle of tiles: the first and last column and row, both inclusive.
 */
export interface TileBounds {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/**
 * The collision cells of the inclusive tile rectangle between two tile corners, in either order, clipped to
 * the map.
 *
 * @param tx0 First corner's tile column.
 * @param ty0 First corner's tile row.
 * @param tx1 Second corner's tile column.
 * @param ty1 Second corner's tile row.
 * @param tilesWide Map width in tiles.
 * @param tilesHigh Map height in tiles.
 * @returns Collision cell indices in row-major order.
 */
export function tileRectCells(tx0: number, ty0: number, tx1: number, ty1: number, tilesWide: number, tilesHigh: number): number[] {
  const n = COLLISION_CELLS_PER_TILE;
  const width = tilesWide * n;
  const left = Math.max(0, Math.min(tx0, tx1)) * n;
  const right = (Math.min(tilesWide - 1, Math.max(tx0, tx1)) + 1) * n;
  const top = Math.max(0, Math.min(ty0, ty1)) * n;
  const bottom = (Math.min(tilesHigh - 1, Math.max(ty0, ty1)) + 1) * n;
  const cells: number[] = [];
  for (let y = top; y < bottom; y += 1) {
    for (let x = left; x < right; x += 1) {
      cells.push(y * width + x);
    }
  }
  return cells;
}

/**
 * Every collision cell of each tile in a list, sorted and without duplicates.
 *
 * @param tiles Tile indices in a `tilesWide` wide grid.
 * @param tilesWide Map width in tiles.
 * @returns Collision cell indices in row-major order.
 */
function cellsOfTiles(tiles: readonly number[], tilesWide: number): number[] {
  const n = COLLISION_CELLS_PER_TILE;
  const width = tilesWide * n;
  const cells: number[] = [];
  for (const tile of tiles) {
    const tx = tile % tilesWide;
    const ty = (tile - tx) / tilesWide;
    for (let y = ty * n; y < (ty + 1) * n; y += 1) {
      for (let x = tx * n; x < (tx + 1) * n; x += 1) {
        cells.push(y * width + x);
      }
    }
  }
  return cells.sort((a, b) => a - b);
}

/**
 * The collision cells one zone stamp covers: the brush size's square of tiles around the tile holding a
 * collision cell, extending right and down for an even size as the tile brush does.
 *
 * @param cx Collision cell column under the pointer.
 * @param cy Collision cell row.
 * @param size Brush size in tiles.
 * @param tilesWide Map width in tiles.
 * @param tilesHigh Map height in tiles.
 * @returns Collision cell indices in row-major order.
 * @throws Error when the size is outside the brush size range.
 */
export function zoneStampCells(cx: number, cy: number, size: number, tilesWide: number, tilesHigh: number): number[] {
  const tx = Math.floor(cx / COLLISION_CELLS_PER_TILE);
  const ty = Math.floor(cy / COLLISION_CELLS_PER_TILE);
  return cellsOfTiles(cellFootprint(tx, ty, size, tilesWide, tilesHigh), tilesWide);
}

/**
 * The tile footprint of one zone stamp as inclusive tile bounds, the rectangle the brush cursor outlines in
 * Zones mode (ADR-054's amendment of 2026-10-01, P1.6-T65): the bounds of the tiles `zoneStampCells` paints at
 * the same cell, size and map.
 *
 * @param cx Collision cell column under the pointer.
 * @param cy Collision cell row.
 * @param size Brush size in tiles.
 * @param tilesWide Map width in tiles.
 * @param tilesHigh Map height in tiles.
 * @returns The first and last tile column and row the stamp covers, or null when it covers no tile.
 * @throws Error when the size is outside the brush size range.
 */
export function zoneStampBounds(cx: number, cy: number, size: number, tilesWide: number, tilesHigh: number): TileBounds | null {
  const tx = Math.floor(cx / COLLISION_CELLS_PER_TILE);
  const ty = Math.floor(cy / COLLISION_CELLS_PER_TILE);
  const tiles = cellFootprint(tx, ty, size, tilesWide, tilesHigh);
  const first = tiles[0];
  const last = tiles[tiles.length - 1];
  if (first === undefined || last === undefined) {
    return null;
  }
  return { x0: first % tilesWide, y0: Math.floor(first / tilesWide), x1: last % tilesWide, y1: Math.floor(last / tilesWide) };
}

/**
 * The collision cells a zone line covers: a connected line from the tile holding the start cell to the tile
 * holding the end cell, the size's square of tiles at each step.
 *
 * @param x0 Start collision cell column.
 * @param y0 Start collision cell row.
 * @param x1 End collision cell column.
 * @param y1 End collision cell row.
 * @param size Brush size in tiles, the line's thickness.
 * @param tilesWide Map width in tiles.
 * @param tilesHigh Map height in tiles.
 * @returns Collision cell indices in row-major order.
 * @throws Error when the size is outside the brush size range.
 */
export function zoneLineCells(x0: number, y0: number, x1: number, y1: number, size: number, tilesWide: number, tilesHigh: number): number[] {
  const n = COLLISION_CELLS_PER_TILE;
  return cellsOfTiles(lineCells(Math.floor(x0 / n), Math.floor(y0 / n), Math.floor(x1 / n), Math.floor(y1 / n), size, tilesWide, tilesHigh), tilesWide);
}
